/**
 * LoginForm — shadcn login-04 layout adapted for Redefining Normal™.
 *
 * - Email + password login
 * - "Send me a magic code" fallback (passwordless)
 * - No social / OAuth buttons
 * - Right panel: brand beach photo with humanistic caption
 */
import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { fx } from "@/lib/effects";
import { Eye, EyeOff, Loader2, KeyRound, Mail, type LucideIcon } from "lucide-react";

const BRAND_IMAGE =
  "https://files.manuscdn.com/user_upload_by_module/session_file/310519663270229297/eKPnmsIVFwQHdlDL.webp";
const BRAND_LOGO =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663270229297/i88gywhNYafBWiKD3FZzYw/lb-logo_6a402776.png";

type Mode = "password" | "magic-request" | "magic-verify";
type PendingAction = Mode | "session-refresh" | null;

interface LoginFormProps extends React.ComponentProps<"div"> {
  onSuccess: () => void | Promise<void>;
}

interface RememberMeToggleProps {
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}

function RememberMeToggle({ checked, disabled, onCheckedChange }: RememberMeToggleProps) {
  return (
    <div className="flex min-h-14 items-center justify-between gap-4 rounded-lg border border-border/70 bg-muted/40 px-3 py-2.5 transition-colors duration-150 focus-within:border-primary/50 focus-within:bg-primary/[0.04]">
      <div className="min-w-0">
        <Label htmlFor="remember-me" className="cursor-pointer text-sm font-medium text-foreground">
          Remember me
        </Label>
        <p id="remember-me-description" className="mt-0.5 text-xs leading-4 text-muted-foreground">
          Stay signed in for 7 days on this device.
        </p>
      </div>
      <Switch
        id="remember-me"
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-describedby="remember-me-description"
        className="h-6 w-10 border-border/60 shadow-sm data-[state=unchecked]:bg-muted-foreground/30 [&_[data-slot=switch-thumb]]:size-5"
      />
    </div>
  );
}

interface AuthSubmitContentProps {
  icon: LucideIcon;
  isPending: boolean;
  idleLabel: string;
  pendingLabel: string;
}

function AuthSubmitContent({ icon: Icon, isPending, idleLabel, pendingLabel }: AuthSubmitContentProps) {
  return (
    <span className="relative z-10 flex items-center justify-center gap-2">
      <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden="true">
        {isPending ? (
          <Loader2 className="size-4 animate-spin motion-reduce:animate-none" />
        ) : (
          <Icon className="size-4" />
        )}
      </span>
      <span className="min-w-36 text-center transition-opacity duration-150 motion-reduce:transition-none">
        {isPending ? pendingLabel : idleLabel}
      </span>
    </span>
  );
}

export function LoginForm({ className, onSuccess, ...props }: LoginFormProps) {
  const [mode, setMode] = useState<Mode>("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const isPending = pendingAction !== null;

  // ── Handlers ──────────────────────────────────────────────────────────────

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    if (isPending) return;
    setError(null);
    setPendingAction("password");
    try {
      const loginResponse = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase(), password, rememberMe }),
      });
      const loginPayload = await loginResponse.json();
      if (!loginResponse.ok) throw new Error(loginPayload.error ?? "Login failed");
      setPendingAction("session-refresh");
      await onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Login failed");
    } finally {
      setPendingAction(null);
    }
  }

  async function handleMagicRequest(e: React.FormEvent) {
    e.preventDefault();
    if (isPending) return;
    setError(null);
    setPendingAction("magic-request");
    try {
      const requestResponse = await fetch("/api/auth/magic-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      const requestPayload = await requestResponse.json();
      if (!requestResponse.ok) throw new Error(requestPayload.error ?? "Request failed");
      setInfo("Check your inbox — a 6-digit code is on its way.");
      setMode("magic-verify");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Request failed");
    } finally {
      setPendingAction(null);
    }
  }

  async function handleMagicVerify(e: React.FormEvent) {
    e.preventDefault();
    if (isPending) return;
    setError(null);
    setPendingAction("magic-verify");
    try {
      const verificationResponse = await fetch("/api/auth/magic-verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: email.trim().toLowerCase(), code: code.trim(), rememberMe }),
      });
      const verificationPayload = await verificationResponse.json();
      if (!verificationResponse.ok) throw new Error(verificationPayload.error ?? "Verification failed");
      setPendingAction("session-refresh");
      await onSuccess();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Verification failed");
    } finally {
      setPendingAction(null);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden shadow-xl border-0">
        <CardContent className="grid p-0 md:grid-cols-2">

          {/* ── Left: form ── */}
          <div className="p-8 flex flex-col justify-center">

            {/* Brand header */}
            <div className="flex items-center gap-3 mb-8">
              <img src={BRAND_LOGO} alt="Lola & Ber" className="h-9 w-9 rounded-md object-contain" />
              <span className="text-sm font-semibold tracking-wide text-foreground">Lola &amp; Ber</span>
            </div>

            {/* Title */}
            <div key={`heading-${mode}`} className="auth-panel-enter mb-6">
              <h1 className="text-2xl font-bold text-foreground">
                {mode === "magic-verify" ? "Enter your code" : "Welcome back"}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {mode === "password" && "Sign in to the Redefining Normal™ platform."}
                {mode === "magic-request" && "We'll email you a one-time sign-in code."}
                {mode === "magic-verify" && "Enter the 6-digit code we sent to your email."}
              </p>
            </div>

            {/* ── Password form ── */}
            {mode === "password" && (
              <form onSubmit={handlePasswordLogin} className="auth-panel-enter flex flex-col gap-5" aria-busy={isPending}>
                <div className="grid gap-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <div className="grid gap-1.5">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPw ? "text" : "password"}
                      placeholder="••••••••"
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pr-10"
                    />
                    <button
                      type="button"
                      aria-label={showPw ? "Hide password" : "Show password"}
                      onClick={() => setShowPw((v) => !v)}
                      className="pw-eye-toggle absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors p-0.5"
                    >
                      {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <RememberMeToggle checked={rememberMe} disabled={isPending} onCheckedChange={setRememberMe} />

                <Button
                  type="submit"
                  className={cn("auth-submit w-full", fx.cta)}
                  disabled={isPending}
                  data-pending={pendingAction === "password" || pendingAction === "session-refresh"}
                >
                  <AuthSubmitContent
                    icon={KeyRound}
                    isPending={pendingAction === "password" || pendingAction === "session-refresh"}
                    idleLabel="Sign in"
                    pendingLabel={pendingAction === "session-refresh" ? "Opening your workspace…" : "Signing in…"}
                  />
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    disabled={isPending}
                    onClick={() => { setMode("magic-request"); setError(null); }}
                    className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors disabled:pointer-events-none disabled:opacity-50"
                  >
                    No password? Send me a magic code
                  </button>
                </div>
              </form>
            )}

            {/* ── Magic request form ── */}
            {mode === "magic-request" && (
              <form onSubmit={handleMagicRequest} className="auth-panel-enter flex flex-col gap-5" aria-busy={isPending}>
                <div className="grid gap-1.5">
                  <Label htmlFor="email-magic">Email</Label>
                  <Input
                    id="email-magic"
                    type="email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <RememberMeToggle checked={rememberMe} disabled={isPending} onCheckedChange={setRememberMe} />

                <Button
                  type="submit"
                  className={cn("auth-submit w-full", fx.cta)}
                  disabled={isPending}
                  data-pending={pendingAction === "magic-request"}
                >
                  <AuthSubmitContent
                    icon={Mail}
                    isPending={pendingAction === "magic-request"}
                    idleLabel="Send magic code"
                    pendingLabel="Sending secure code…"
                  />
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    disabled={isPending}
                    onClick={() => { setMode("password"); setError(null); }}
                    className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors disabled:pointer-events-none disabled:opacity-50"
                  >
                    Back to password sign-in
                  </button>
                </div>
              </form>
            )}

            {/* ── Magic verify form ── */}
            {mode === "magic-verify" && (
              <form onSubmit={handleMagicVerify} className="auth-panel-enter flex flex-col gap-5" aria-busy={isPending}>
                {info && <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">{info}</p>}
                <div className="grid gap-1.5">
                  <Label htmlFor="code">6-digit code</Label>
                  <Input
                    id="code"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="123456"
                    autoComplete="one-time-code"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    className="text-center text-2xl tracking-[0.4em] font-mono"
                  />
                </div>

                {error && <p className="text-sm text-destructive">{error}</p>}

                <RememberMeToggle checked={rememberMe} disabled={isPending} onCheckedChange={setRememberMe} />

                <Button
                  type="submit"
                  className={cn("auth-submit w-full", fx.cta)}
                  disabled={isPending}
                  data-pending={pendingAction === "magic-verify" || pendingAction === "session-refresh"}
                >
                  <AuthSubmitContent
                    icon={KeyRound}
                    isPending={pendingAction === "magic-verify" || pendingAction === "session-refresh"}
                    idleLabel="Verify & sign in"
                    pendingLabel={pendingAction === "session-refresh" ? "Opening your workspace…" : "Verifying code…"}
                  />
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    disabled={isPending}
                    onClick={() => { setMode("magic-request"); setCode(""); setError(null); setInfo(null); }}
                    className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors disabled:pointer-events-none disabled:opacity-50"
                  >
                    Resend code
                  </button>
                  <span className="mx-2 text-muted-foreground">·</span>
                  <button
                    type="button"
                    disabled={isPending}
                    onClick={() => { setMode("password"); setCode(""); setError(null); setInfo(null); }}
                    className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4 transition-colors disabled:pointer-events-none disabled:opacity-50"
                  >
                    Use password instead
                  </button>
                </div>
              </form>
            )}

            {/* Footer */}
            <div className="mt-8 flex flex-col gap-2">
              <p className="text-xs text-muted-foreground">
                Lola &amp; Ber · What If · © 2026
              </p>
              <p className="text-xs text-muted-foreground">
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-4 hover:text-foreground transition-colors"
                >
                  Privacy Policy
                </a>
                {" · "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-4 hover:text-foreground transition-colors"
                >
                  Terms of Service
                </a>
              </p>
            </div>
          </div>

          {/* ── Right: brand image ── */}
          <div className="relative hidden md:block">
            <img
              src={BRAND_IMAGE}
              alt="People celebrating on the beach at sunset"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute bottom-0 left-0 right-0 p-8 bg-gradient-to-t from-black/75 to-transparent">
              <blockquote className="text-white">
                <p className="text-base font-semibold leading-snug tracking-wide">
                  Most people don&rsquo;t fit the old normal.
                </p>
                <p className="mt-1.5 text-sm font-normal leading-relaxed text-white/85">
                  We&rsquo;re here to show the world what the new one looks like.
                </p>
                <footer className="mt-3 text-xs text-white/60 font-medium tracking-wide uppercase">
                  Redefining Normal™ · Lola &amp; Ber
                </footer>
              </blockquote>
            </div>
          </div>

        </CardContent>
      </Card>

      {/* Powered by Norfolk AI */}
      <div className="mt-4 flex items-center justify-center gap-2 opacity-60 hover:opacity-90 transition-opacity">
        <img
          src="https://d2xsxph8kpxj0f.cloudfront.net/310519663270229297/i88gywhNYafBWiKD3FZzYw/brand/norfolk-ai-logo_24f57943.png"
          alt="Norfolk AI"
          className="h-5 w-5 object-contain"
        />
        <span className="text-xs text-muted-foreground font-medium tracking-wide">
          Powered by <span className="font-semibold">Norfolk AI</span>
        </span>
      </div>
    </div>
  );
}
